import { CheckMarkIcon, ChevronRightIcon } from "/icons";
import { Image } from "/image";
import { formatMoney } from "/utils";
import { ProductBlocksAdvanced_subscription } from "_types";
import clsx from "clsx";
import { ReactProductState } from "product";
import React, { FC, useRef } from "react";
import { StoreApi, UseBoundStore } from "zustand";

export const ProductAdvancedSubscription: FC<{
  settings: ProductBlocksAdvanced_subscription["settings"];
  useProduct: UseBoundStore<StoreApi<ReactProductState>>;
}> = ({ settings, useProduct }) => {
  const selectRef = useRef<HTMLSelectElement>(null);
  const subscriptionContentRef = useRef<HTMLDivElement>(null);
  const noSubscriptionContentRef = useRef<HTMLDivElement>(null);
  const { product, setSelectedSellingPlan, selectedSellingPlan, selectedVariant, formId } =
    useProduct();

  const discount = selectedSellingPlan
    ? Math.round(
        ((selectedSellingPlan?.compare_at_price - selectedSellingPlan?.price) /
          selectedSellingPlan?.compare_at_price) *
          100
      )
    : selectedVariant?.selling_plan_allocations?.reduce((acc, plan) => {
        const discount = Math.round(
          ((plan?.compare_at_price - plan?.price) / plan?.compare_at_price) * 100
        );
        if (discount || discount < acc) {
          acc = discount;
        }
        return acc;
      }, 0) ?? 0;

  if (!product?.variants.some((v) => v.selling_plan_allocations?.length)) {
    return null;
  }

  const selling_plan_allocations = selectedVariant?.selling_plan_allocations;

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm">Frequency</h3>
      <fieldset className="flex flex-col gap-2">
        <label
          className={clsx(
            "flex flex-col rounded-theme-md border px-4 py-3",
            !selectedSellingPlan
              ? "cursor-pointer border-gray-200"
              : "border-primary-outline bg-secondary-bg/20"
          )}
        >
          <input
            type="radio"
            name={`advanced_subscription_selector-${formId}`}
            className="hidden appearance-none"
            value="subscription"
            onChange={(e) => {
              setSelectedSellingPlan(
                selling_plan_allocations?.find((plan) => plan.id === +selectRef?.current?.value)
              );
            }}
          />
          <div className="flex items-center gap-3">
            <span
              className={clsx(
                "group flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden rounded-full border",
                selectedSellingPlan ? "border-primary-bg bg-primary-bg" : "border-gray-500"
              )}
            >
              {selectedSellingPlan ? <CheckMarkIcon className="h-2 w-2 text-white" /> : null}
            </span>
            <span className={clsx("text-xs md:text-sm", selectedSellingPlan ? "font-bold" : "")}>
              {settings.subscription_text}
            </span>
            {discount > 0
              ? <span className="rounded-[60px] bg-secondary-bg px-3 py-1 text-xs font-bold leading-normal text-primary-text">
                  {discount}% OFF
                </span>
              : null}
            <span className="flex-1"></span>
            <div className="flex shrink-0 items-baseline gap-1 text-xs md:text-sm ">
              {discount
                ? <>
                    <span
                      className="text-theme-text/50 line-through"
                      dangerouslySetInnerHTML={{ __html: formatMoney(selectedVariant.price) }}
                    ></span>
                    <span
                      className="font-bold"
                      dangerouslySetInnerHTML={{
                        __html: formatMoney(
                          selectedVariant.price - selectedVariant.price * (discount / 100)
                        ),
                      }}
                    ></span>
                  </>
                : <span
                    className="font-bold"
                    dangerouslySetInnerHTML={{ __html: formatMoney(selectedVariant.price) }}
                  ></span>}
            </div>
          </div>
          <div
            className={clsx(
              "flex flex-col gap-3 overflow-hidden transition-all duration-150 ease-linear"
            )}
            style={{
              maxHeight: selectedSellingPlan
                ? `${(subscriptionContentRef?.current?.scrollHeight ?? 999) + 10}px`
                : 0,
            }}
            ref={subscriptionContentRef}
          >
            <div className="flex flex-col px-6">
              {settings.icon_1 || settings.content_1
                ? <div className="mt-3 flex items-center gap-2 text-xs">
                    {settings.icon_1
                      ? <Image
                          src={settings.icon_1 as string}
                          alt=""
                          className="h-4 w-4"
                          width={30}
                        />
                      : null}

                    <span>{settings.content_1}</span>
                  </div>
                : null}
              {settings.icon_2 || settings.content_2
                ? <div className="mt-3 flex items-center gap-2 text-xs">
                    {settings.icon_2
                      ? <Image
                          src={settings.icon_2 as string}
                          alt=""
                          className="h-4 w-4"
                          width={30}
                        />
                      : null}

                    <span>{settings.content_2}</span>
                  </div>
                : null}
              {settings.icon_3 || settings.content_3
                ? <div className="mt-3 flex items-center gap-2 text-xs">
                    {settings.icon_3
                      ? <Image
                          src={settings.icon_3 as string}
                          alt=""
                          className="h-4 w-4"
                          width={30}
                        />
                      : null}

                    <span>{settings.content_3}</span>
                  </div>
                : null}
            </div>
            <label className="relative flex w-full items-center gap-1 rounded-theme-md border border-black/20 bg-theme-bg pl-5 text-sm leading-[1.35]">
              <span className="font-bold">Delivery</span>
              <select
                ref={selectRef}
                className="relative w-full appearance-none rounded-theme-md py-3 pr-12 hf:outline-none"
                onChange={(e) => {
                  const newSellingPlanId = e.currentTarget?.value;
                  setSelectedSellingPlan(
                    selling_plan_allocations?.find((plan) => plan.id === +newSellingPlanId)
                  );
                }}
                value={selectedSellingPlan?.id}
              >
                {selling_plan_allocations?.map((sellingPlan) => {
                  return (
                    <option
                      key={sellingPlan?.id}
                      value={sellingPlan?.id}
                      className="disabled:text-theme-text/50"
                      selected={selectedSellingPlan?.id === sellingPlan?.id}
                    >
                      {sellingPlan?.name?.replace(/deliv[^\s]*/gi, "")}
                    </option>
                  );
                })}
              </select>
              <ChevronRightIcon className="pointer-events-none absolute right-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 rotate-90" />
            </label>
          </div>
        </label>
        <label
          className={clsx(
            "flex flex-col rounded-theme-md border px-4 py-3",
            selectedSellingPlan ? "cursor-pointer border-gray-200" : "border-black"
          )}
        >
          <input
            type="radio"
            name={`advanced_subscription_selector-${formId}`}
            className="hidden appearance-none"
            value="no_subscription"
            onChange={(e) => {
              setSelectedSellingPlan(null);
            }}
          />
          <div className="flex items-center gap-3">
            <span
              className={clsx(
                "group flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden rounded-full border",
                !selectedSellingPlan ? "border-primary-bg bg-primary-bg" : "border-gray-500"
              )}
            >
              {!selectedSellingPlan ? <CheckMarkIcon className="h-2 w-2 text-white" /> : null}
            </span>
            <span className={clsx("text-sm", !selectedSellingPlan ? "font-bold" : "")}>
              {settings.no_subscription_text}
            </span>
            <span className="flex-1"></span>
            <div className="flex shrink-0 items-center gap-2">
              <span
                className="text-sm font-bold"
                dangerouslySetInnerHTML={{ __html: formatMoney(selectedVariant.price) }}
              />
            </div>
          </div>
          <div
            ref={noSubscriptionContentRef}
            className={clsx(
              "flex flex-col gap-3 overflow-hidden transition-all duration-150 ease-linear"
            )}
            style={{
              maxHeight: !selectedSellingPlan
                ? `${(noSubscriptionContentRef?.current?.scrollHeight ?? 999) + 10}px`
                : 0,
            }}
          >
            <div className="flex flex-col px-6">
              {settings.icon_4 || settings.content_4
                ? <div className="mt-3 flex items-center gap-2 text-xs">
                    {settings.icon_4
                      ? <Image
                          src={settings.icon_4 as string}
                          alt=""
                          className="h-4 w-4"
                          width={30}
                        />
                      : null}
                    <span>{settings.content_4}</span>
                  </div>
                : null}
              {settings.icon_5 || settings.content_5
                ? <div className="mt-3 flex items-center gap-2 text-xs">
                    {settings.icon_5
                      ? <Image
                          src={settings.icon_5 as string}
                          alt=""
                          className="h-4 w-4"
                          width={30}
                        />
                      : null}
                    <span>{settings.content_5}</span>
                  </div>
                : null}
              {settings.icon_6 || settings.content_6
                ? <div className="mt-3 flex items-center gap-2 text-xs">
                    {settings.icon_6
                      ? <Image
                          src={settings.icon_6 as string}
                          alt=""
                          className="h-4 w-4"
                          width={30}
                        />
                      : null}
                    <span>{settings.content_6}</span>
                  </div>
                : null}
            </div>
          </div>
        </label>
      </fieldset>
    </div>
  );
};
